import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SelectOption } from '../../../model/selectOption'
import { useRootWbsItems } from './useRootWbsItems'
import { pruneRootUuids } from '../wbsItemSearch'

type ProcessScopeSearchState = {
  processOptions: SelectOption[]
  processParentMap: Record<string, string>
  processOptionsLoading: boolean
  includeDeliverableOptions: boolean
  onToggleIncludeDeliverableOptions: (checked: boolean) => void
  isDeliverableSelected: boolean
  isToggleDisabled: boolean
  prefetchProcessScopeOptions: () => void
}

// 詳細条件モーダル・検索画面ヘッダーの条件チップの両方から「成果物を含める」
// モードを共有で使うための状態。チップは検索画面を開いている間ずっと
// マウントされ続けるため、モーダルのように「開いている間だけ取得する」という
// 前提が使えず、projectUuidのみをガードとして常時この状態を保持する。
export const useProcessScopeSearchState = (
  projectUuid: string,
  rootUuids: string[] | undefined,
  onChangeRootUuids: (newValue: string[] | undefined) => void
): ProcessScopeSearchState => {
  const [processOptions, setProcessOptions] = useState<SelectOption[]>([])
  const [processParentMap, setProcessParentMap] = useState<
    Record<string, string>
  >({})
  const [processOptionsLoading, setProcessOptionsLoading] =
    useState<boolean>(false)
  const [includeDeliverableOptions, setIncludeDeliverableOptions] =
    useState<boolean>(false)
  const getRootWbsItems = useRootWbsItems(
    projectUuid,
    includeDeliverableOptions
  )

  // includeDeliverableOptions=ON時のみ、選択済みuuidの中に成果物単体が
  // 含まれているかを判定するため、常時取得している拡張版(4種類)とは別に
  // 基本3種類のみの一覧も取得する。OFF時は下のeffectが基本一覧に無い
  // uuidを検知した時点で自動的にONへ切り替えるため、OFFの間は成果物単体が
  // 選択され続けることはなく、この判定用フェッチ自体が不要
  // （nullは「まだ判定結果が確定していない」を表し、これをfalse扱いする
  //  ことで、トグルON直後・空集合との誤った差分判定によるタイトルの
  //  ちらつきを防ぐ）
  const [baseRootUuidSet, setBaseRootUuidSet] = useState<Set<string> | null>(
    null
  )
  const getBaseRootWbsItems = useRootWbsItems(projectUuid, false)
  const getDeliverableRootWbsItems = useRootWbsItems(projectUuid, true)

  // 成果物込みの一覧は全件取得のため数秒かかる。トグルを押してから取得を
  // 始めると待たされるので、ダイアログ・ポップオーバーを開いた時点で
  // useRootWbsItems の共有キャッシュを温めておき、切替時の取得はキャッシュ
  // （取得中ならその Promise）を受け取るだけにする。
  // 先読みはユーザーが頼んだ操作ではなく、失敗したエントリはキャッシュから
  // 破棄されて実際の切替時に再取得されるため、Snackbar では通知しない
  const prefetchProcessScopeOptions = useCallback(() => {
    if (!projectUuid) return
    Promise.all([getDeliverableRootWbsItems(), getBaseRootWbsItems()]).catch(
      error => console.error('Failed to prefetch process scope options:', error)
    )
  }, [projectUuid, getDeliverableRootWbsItems, getBaseRootWbsItems])

  const rootUuidsRef = useRef(rootUuids)
  rootUuidsRef.current = rootUuids

  // ユーザーが「成果物を含める」を明示的にOFFへ切り替えたときだけtrueにする。
  // 次の取得完了時にこの印を見て、rootUuidsのpruneを行ってよいか判断する
  // （stateにすると変更のたびに再レンダリング・再取得を招くためrefで持つ）
  const pruneOnNextFetchRef = useRef(false)

  const onToggleIncludeDeliverableOptions = useCallback((checked: boolean) => {
    if (!checked) {
      pruneOnNextFetchRef.current = true
    }
    setIncludeDeliverableOptions(checked)
  }, [])

  useEffect(() => {
    if (!projectUuid) return
    // 成果物を含めるモードを素早く切り替えた場合、古いリクエストの結果が
    // 後から返ってきて新しい状態を上書きしてしまわないよう、
    // このeffectが再実行/破棄された後の結果は無視する
    const cancelled = { current: false }
    setProcessOptionsLoading(true)
    getRootWbsItems()
      .then(results => {
        if (cancelled.current) return
        setProcessOptions(
          results.map(item => ({
            value: item.uuid,
            name: item.name,
            iconUrl: item.iconUrl,
            tooltip: item.path,
          }))
        )
        setProcessParentMap(
          Object.fromEntries(
            results
              .filter(item => item.parentUuid)
              .map(item => [item.uuid, item.parentUuid!])
          )
        )
        // 印は取得1回ごとに使い切る。一覧に無い選択が無かったときに残すと、
        // 後の取得で、ユーザーがOFFにしていないのに選択を取り除いてしまう
        const isPruneRequested = pruneOnNextFetchRef.current
        pruneOnNextFetchRef.current = false

        // 成果物を含まない一覧に無い選択は、成果物単体の可能性があるため
        // ここでは取り除かない（下のeffectがモードをONにして取り直す）。
        // 取り除くのは、ユーザーが明示的にOFFにした場合と、成果物を含む
        // 一覧で確認してもなお存在しない（WBSアイテムが削除された等）場合のみ
        if (!isPruneRequested && !includeDeliverableOptions) return
        const validUuids = new Set(results.map(item => item.uuid))
        // 詳細条件モーダルの全クリアでは空文字が入るため、?? ではなく || で配列にする
        const currentRootUuids = rootUuidsRef.current || []
        if (currentRootUuids.every(uuid => validUuids.has(uuid))) return
        onChangeRootUuids(
          pruneRootUuids(currentRootUuids, uuid => validUuids.has(uuid))
        )
      })
      .finally(() => {
        if (!cancelled.current) setProcessOptionsLoading(false)
      })
    return () => {
      cancelled.current = true
    }
  }, [
    projectUuid,
    getRootWbsItems,
    onChangeRootUuids,
    includeDeliverableOptions,
  ])

  // 成果物を含まない一覧には成果物単体の名前が無く、選択されていてもチップや
  // ツリーに表示できないため、一覧に無いuuidが選ばれていればモードをONにして
  // 成果物込みで取り直す。一覧の取得完了時だけでなく、保存済み検索条件の
  // 呼び出しなどで後からrootUuidsが変わったときにも判定する必要がある
  useEffect(() => {
    if (includeDeliverableOptions || processOptionsLoading) return
    // 初回取得前（一覧が空）に判定すると、すべての選択が一覧に無い扱いになる
    if (processOptions.length === 0) return
    const optionUuids = new Set(processOptions.map(option => option.value))
    // 詳細条件モーダルの全クリアでは空文字が入るため、?? ではなく || で配列にする
    // （空文字のまま .some を呼ぶとeffect内で例外になり、画面全体が描画されなくなる）
    const hasUnknownSelection = (rootUuids || []).some(
      uuid => !optionUuids.has(uuid)
    )
    if (hasUnknownSelection) setIncludeDeliverableOptions(true)
  }, [
    includeDeliverableOptions,
    processOptionsLoading,
    processOptions,
    rootUuids,
  ])

  useEffect(() => {
    if (!projectUuid || !includeDeliverableOptions) {
      setBaseRootUuidSet(null)
      return
    }
    const cancelled = { current: false }
    getBaseRootWbsItems().then(results => {
      if (cancelled.current) return
      setBaseRootUuidSet(new Set(results.map(item => item.uuid)))
    })
    return () => {
      cancelled.current = true
    }
  }, [projectUuid, includeDeliverableOptions, getBaseRootWbsItems])

  // uuidごとの個別フェッチはせず、基本3種類一覧に無い・かつ拡張一覧
  // (processOptions)には有る、という差分で成果物単体の選択有無を判定する。
  // processOptionsとの突き合わせは、削除済み等の無効uuidを誤って
  // 「成果物」と判定しないためのガード
  const isDeliverableSelected = useMemo(() => {
    if (!includeDeliverableOptions || baseRootUuidSet === null) return false
    const currentRootUuids = rootUuids || []
    return currentRootUuids.some(
      uuid =>
        !baseRootUuidSet.has(uuid) &&
        processOptions.some(option => option.value === uuid)
    )
  }, [includeDeliverableOptions, baseRootUuidSet, rootUuids, processOptions])

  // 成果物を選択したままOFFにすると、選択が黙って検索条件から外れてしまい
  // UX的に良くないため、成果物が選択されている間はトグル自体を無効化する。
  // isDeliverableSelectedは判定用一覧の取得が揃うまでfalseを返す仕様なので、
  // そのままdisabled判定に使うと一覧取得中だけ一瞬OFFにできてしまう。
  // そのため取得中（processOptionsLoading・baseRootUuidSet未確定）も
  // 合わせて無効化しておく
  const isToggleDisabled = useMemo(() => {
    if (!includeDeliverableOptions) return false
    if (processOptionsLoading || baseRootUuidSet === null) return true
    return isDeliverableSelected
  }, [
    includeDeliverableOptions,
    processOptionsLoading,
    baseRootUuidSet,
    isDeliverableSelected,
  ])

  return {
    processOptions,
    processParentMap,
    processOptionsLoading,
    includeDeliverableOptions,
    onToggleIncludeDeliverableOptions,
    isDeliverableSelected,
    isToggleDisabled,
    prefetchProcessScopeOptions,
  }
}
